/**
 * Shared fetch helper for back-office API calls.
 *
 * Auth flow (DEV-125):
 *   - The access token lives in this module's memory only, never in storage.
 *   - The refresh token is an HttpOnly cookie (`norvix_admin_rt`) that no
 *     script here can read; the browser attaches it to /admin/auth/refresh.
 *   - On page load, and on a 401, we renew once from that cookie. If renewal
 *     succeeds we replay the original request transparently; if it is
 *     rejected we end the session. Temporary failures preserve it for retry.
 */

import { API_BASE_URL } from '../config/env.js';

export interface ApiEnvelope<T> {
  data: T | null;
  meta?: {
    total?: number;
    page?: number;
    limit?: number;
    hasNext?: boolean;
    /** Offset-paged endpoints (e.g. the client payment history). */
    offset?: number;
    hasMore?: boolean;
    /** A merged/paged list may be missing older rows from a very busy source. */
    truncated?: boolean;
    /** A merged list where at least one source failed to answer — partial data. */
    degraded?: boolean;
    /** Unread notifications, on the notification feed. */
    unread?: number;
  };
  error?: {
    type: string;
    title: string;
    status: number;
    code: string;
    detail?: string;
  };
}

/**
 * Where the back office kept both tokens before DEV-125. Nothing is written
 * here any more; a record left behind is removed on sight.
 */
export const LEGACY_SESSION_STORAGE_KEY = 'norvix.backoffice.session.v1';

/** Preserve HTTP status so auth can distinguish rejection from an outage. */
export class ApiError extends Error {
  readonly retryAt: number | null;
  constructor(message: string, readonly status: number, retryAfterMs?: number, readonly code?: string) {
    super(message);
    this.name = 'ApiError';
    this.retryAt = status === 429 ? Date.now() + (retryAfterMs ?? 60_000) : null;
  }
}

function retryDelay(response: Response): number | undefined {
  const value = response.headers.get('Retry-After');
  if (!value) return undefined;
  const delay = /^\d+$/.test(value) ? Number(value) * 1000 : Date.parse(value) - Date.now();
  return Number.isFinite(delay) ? Math.max(1000, delay) : undefined;
}

function isAuthEndpoint(path: string): boolean {
  return path.includes('/admin/auth/login') || path.includes('/admin/auth/refresh') ||
    path === '/admin/auth/totp/verify-setup' || path === '/admin/auth/logout';
}

let accessToken: string | null = null;

/** Set by AuthProvider after login / refresh. */
export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

/**
 * Hook the AuthProvider uses to perform a silent refresh on 401. We inject
 * it from outside to keep this module free of React-state coupling.
 */
type RefreshHook = () => Promise<string | null>;
let refreshHook: RefreshHook | null = null;
export function setRefreshHook(hook: RefreshHook | null): void {
  refreshHook = hook;
}

interface RequestInitWithBody extends Omit<RequestInit, 'body'> {
  body?: unknown;
  /** When true, the 401-then-refresh-and-retry interceptor is skipped. */
  skipAuthRefresh?: boolean;
}

// Single in-flight refresh shared by every concurrent 401 — without this, a
// page with N parallel queries would burn N refresh tokens (and trigger the
// REFRESH_TOKEN_REUSED guard).
let inFlightRefresh: Promise<string | null> | null = null;

/** The Web Lock that keeps two tabs from spending one refresh cookie at once. */
const REFRESH_LOCK = 'norvix.backoffice.auth.refresh';

interface LockManagerLike {
  request: (name: string, callback: () => Promise<unknown>) => Promise<unknown>;
}

function lockManager(): LockManagerLike | null {
  if (typeof navigator === 'undefined') return null;
  const locks = (navigator as { locks?: LockManagerLike }).locks;
  return locks !== undefined && typeof locks.request === 'function' ? locks : null;
}

/**
 * Renew the session from the refresh cookie: the new access token, null when
 * the cookie was refused, or a rejection when the gateway could not answer.
 *
 * Single-flight in this tab, and serialised across tabs with a Web Lock where
 * the browser has one: refresh tokens are single-use, so a second tab must
 * send the cookie the first one just rotated, not the one it replaced.
 */
export function renewSession(): Promise<string | null> {
  const hook = refreshHook;
  if (hook === null) return Promise.resolve(null);
  inFlightRefresh ??= (async (): Promise<string | null> => {
    const locks = lockManager();
    return locks === null ? hook() : (locks.request(REFRESH_LOCK, hook) as Promise<string | null>);
  })().finally(() => {
    inFlightRefresh = null;
  });
  return inFlightRefresh;
}

const performRefresh = renewSession;

function clearSession(): void {
  try {
    window.localStorage.removeItem(LEGACY_SESSION_STORAGE_KEY);
  } catch {
    // localStorage may be unavailable (SSR / Safari private mode); ignore.
  }
  accessToken = null;

  // And SAY so. Clearing the token silently left the console fully rendered
  // around empty tables: an operator reading "No onboarding applications yet"
  // has no way to tell an empty queue from an expired session, and in a bank
  // "there is nothing to review" is a conclusion somebody acts on.
  //
  // A hard navigation rather than a router push, because this is reached from
  // the fetch layer, which has no router — and because throwing away all
  // in-memory state is the point when a session has ended.
  if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
    window.location.assign(`/login?expired=1&from=${encodeURIComponent(window.location.pathname)}`);
  }
}

async function rawFetch(path: string, init: RequestInitWithBody): Promise<Response> {
  const headers = new Headers(init.headers);
  // FormData carries its own multipart content-type WITH the boundary the
  // parser needs. Setting one here would overwrite it with a value that has no
  // boundary, and the server would reject a body it cannot split.
  const isFormData = init.body instanceof FormData;
  if (init.body !== undefined && !isFormData && !headers.has('content-type')) {
    headers.set('content-type', 'application/json');
  }
  if (accessToken !== null && !headers.has('authorization')) {
    headers.set('authorization', `Bearer ${accessToken}`);
  }
  const body = isFormData
    ? (init.body as BodyInit)
    : init.body !== undefined && typeof init.body !== 'string'
      ? JSON.stringify(init.body)
      : (init.body as BodyInit | undefined);

  const { body: _b, skipAuthRefresh: _s, ...restInit } = init;
  void _b; void _s;
  return fetch(`${API_BASE_URL}${path}`, {
    ...restInit,
    headers,
    credentials: 'include',
    ...(body !== undefined ? { body } : {}),
  });
}

export async function apiFetch<T>(
  path: string,
  init: RequestInitWithBody = {},
): Promise<T> {
  let response = await rawFetch(path, init);

  // 401 → attempt one silent refresh + replay, unless this IS the refresh call
  // or the caller opted out.
  if (
    response.status === 401 &&
    init.skipAuthRefresh !== true &&
    !path.includes('/admin/auth/login') &&
    !path.includes('/admin/auth/refresh')
  ) {
    const newToken = await performRefresh();
    if (newToken !== null) {
      response = await rawFetch(path, init);
    } else {
      clearSession();
    }
  }

  const envelope = (await response.json().catch(() => ({}))) as ApiEnvelope<T>;

  if (response.status === 401 && !isAuthEndpoint(path)) {
    // Refresh either wasn't attempted (skipped) or failed and the retry also
    // came back 401. Either way the session is dead — clean up.
    clearSession();
  }

  if (!response.ok || envelope.error !== undefined) {
    throw new ApiError(
      envelope.error?.detail ??
        envelope.error?.title ??
        `Request failed: ${String(response.status)}`,
      response.status,
      retryDelay(response),
      envelope.error?.code,
    );
  }
  if (envelope.data === null) {
    throw new Error('Empty response');
  }
  return envelope.data;
}

/**
 * As `apiFetch`, but returns the whole envelope so a caller can read `meta`
 * (pagination) alongside `data`.
 *
 * It refreshes and ends the session on a 401 exactly as `apiFetch` does. It
 * used to do neither: a 401 fell through to `.catch(() => ({ data: null }))`
 * and every list page in the console rendered "nothing here" for a request
 * that was actually rejected. The list pages are precisely the screens where
 * that reads as a fact about the business.
 */
export async function apiFetchRaw<T>(
  path: string,
  init: RequestInitWithBody = {},
): Promise<ApiEnvelope<T>> {
  let response = await rawFetch(path, init);

  if (
    response.status === 401 &&
    init.skipAuthRefresh !== true &&
    !path.includes('/admin/auth/login') &&
    !path.includes('/admin/auth/refresh')
  ) {
    const newToken = await performRefresh();
    if (newToken !== null) {
      response = await rawFetch(path, init);
    }
  }

  if (response.status === 401 && !isAuthEndpoint(path)) {
    clearSession();
    throw new Error('Your session has expired. Please sign in again.');
  }

  return (await response.json().catch(() => ({ data: null }))) as ApiEnvelope<T>;
}

/**
 * Fetch a binary response (e.g. a stored document) as a Blob, with the same
 * Bearer auth + silent-refresh behaviour as apiFetch. Inline elements
 * (img/iframe) can't carry an Authorization header, so callers turn the
 * returned Blob into an object URL.
 */
export async function apiFetchBlob(
  path: string,
  init: RequestInitWithBody = {},
): Promise<Blob> {
  let response = await rawFetch(path, init);

  if (response.status === 401 && init.skipAuthRefresh !== true) {
    const newToken = await performRefresh();
    if (newToken !== null) {
      response = await rawFetch(path, init);
    } else {
      clearSession();
    }
  }

  if (!response.ok) {
    const envelope = (await response.json().catch(() => ({}))) as ApiEnvelope<unknown>;
    throw new ApiError(
      envelope.error?.detail ??
        envelope.error?.title ??
        `Request failed: ${String(response.status)}`,
      response.status,
      retryDelay(response),
    );
  }
  return response.blob();
}

/**
 * POST a multipart form and report upload progress (0–1) as it goes.
 *
 * `fetch` cannot report upload progress, which a bulk upload needs per file
 * (DEV-66), so this uses XMLHttpRequest with the same Bearer auth, the same
 * one silent refresh on 401, and the same error envelope as `apiFetch`.
 */
export async function apiUpload<T>(
  path: string,
  form: FormData,
  onProgress?: (fraction: number) => void,
): Promise<T> {
  const send = (): Promise<{ status: number; body: string; retryAfter: string | null }> =>
    new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API_BASE_URL}${path}`);
      xhr.withCredentials = true;
      if (accessToken !== null) xhr.setRequestHeader('authorization', `Bearer ${accessToken}`);
      xhr.upload.onprogress = (event): void => {
        if (event.lengthComputable && event.total > 0) onProgress?.(event.loaded / event.total);
      };
      xhr.onload = (): void => resolve({ status: xhr.status, body: xhr.responseText, retryAfter: xhr.getResponseHeader('Retry-After') });
      xhr.onerror = (): void => reject(new Error('The connection dropped before the file was sent.'));
      xhr.onabort = (): void => reject(new Error('The upload was cancelled.'));
      xhr.send(form);
    });

  let result = await send();
  if (result.status === 401) {
    const newToken = await performRefresh();
    if (newToken === null) {
      clearSession();
    } else {
      onProgress?.(0);
      result = await send();
    }
  }
  if (result.status === 401) clearSession();

  let envelope: ApiEnvelope<T> = { data: null };
  try {
    envelope = JSON.parse(result.body) as ApiEnvelope<T>;
  } catch {
    // A non-JSON body (a proxy's error page) — the status says enough.
  }
  if (result.status < 200 || result.status >= 300 || envelope.error !== undefined) {
    const retry = result.retryAfter !== null && /^\d+$/.test(result.retryAfter) ? Number(result.retryAfter) * 1000 : undefined;
    throw new ApiError(
      envelope.error?.detail ?? envelope.error?.title ?? `Request failed: ${String(result.status)}`,
      result.status,
      retry,
    );
  }
  if (envelope.data === null) throw new Error('Empty response');
  return envelope.data;
}
